import {
  PLAN_FEATURES,
  PLAN_LIMITS,
  type PlanFeatureKey,
  type UsageMetricSnapshot,
} from '@bbpos/types';

// Display helpers for the plan registry in `@bbpos/types` (docs/plan-limits-plan.md
// §3-§4). The registry owns every key, label and description; this file only
// decides headings and number formats for the platform admin screens.

export type PlanFeature = (typeof PLAN_FEATURES)[number];
export type PlanLimit = (typeof PLAN_LIMITS)[number];

export interface PlanFeatureGroup {
  title: string;
  features: PlanFeature[];
}

/** Which heading each feature sits under in the admin. A key left out still shows, under "Other". */
const FEATURE_GROUPS: ReadonlyArray<{ title: string; keys: readonly PlanFeatureKey[] }> = [
  {
    title: 'Selling',
    keys: ['voice_pos', 'credit_billing', 'quotations', 'corporate_accounts', 'einvoice'],
  },
  { title: 'Customers and messages', keys: ['loyalty', 'memberships', 'marketing', 'whatsapp'] },
  { title: 'Selling online', keys: ['online_store', 'online_payments'] },
  {
    title: 'Stock and buying',
    keys: ['stock_transfers', 'purchase', 'batch_serial', 'demand_forecast'],
  },
  { title: 'Kitchen and events', keys: ['kds', 'festive'] },
  { title: 'Staff', keys: ['commissions', 'staff_attendance'] },
  { title: 'Reports', keys: ['advanced_reports', 'tally_export'] },
];

/** Registry features under their headings, registry order inside each; empty groups dropped. */
export function groupPlanFeatures(
  include: (key: PlanFeatureKey) => boolean = () => true,
): PlanFeatureGroup[] {
  const placed = new Set<string>(FEATURE_GROUPS.flatMap((g) => g.keys));
  const groups = [
    ...FEATURE_GROUPS.map((g) => ({
      title: g.title,
      features: PLAN_FEATURES.filter((f) => (g.keys as readonly string[]).includes(f.key)),
    })),
    { title: 'Other', features: PLAN_FEATURES.filter((f) => !placed.has(f.key)) },
  ];
  return groups
    .map((g) => ({ ...g, features: g.features.filter((f) => include(f.key)) }))
    .filter((g) => g.features.length > 0);
}

/** Registry label for a stored key; an unknown (retired) key shows as itself. */
export function planFeatureLabel(key: string): string {
  return PLAN_FEATURES.find((f) => f.key === key)?.label ?? key.replace(/_/g, ' ');
}

type MeteredLimit = Exclude<PlanLimit, { key: 'locations' | 'users' | 'requests_per_minute' }>;

/**
 * The limits a plan may leave blank (= unlimited) and set to 0 — everything
 * except the two seat limits and the rate limit, which keep their own inputs.
 */
export const METERED_LIMITS: MeteredLimit[] = PLAN_LIMITS.filter(
  (l): l is MeteredLimit =>
    l.key !== 'locations' && l.key !== 'users' && l.key !== 'requests_per_minute',
);

export type MeteredLimitColumn = MeteredLimit['column'];

/** Input label: the registry label, with the unit when it is not a plain count. */
export function limitInputLabel(limit: PlanLimit): string {
  return limit.unit === 'mb' ? `${limit.label} (MB)` : limit.label;
}

const BYTES_PER_MB = 1024 * 1024;

function roundSize(n: number): string {
  const rounded = n < 10 ? Math.round(n * 10) / 10 : Math.round(n);
  return rounded.toLocaleString('en-IN');
}

/** MB as "2 GB" when it divides evenly, otherwise "500 MB". */
export function formatMb(mb: number): string {
  if (mb >= 1024 && mb % 1024 === 0) return `${(mb / 1024).toLocaleString('en-IN')} GB`;
  return `${mb.toLocaleString('en-IN')} MB`;
}

export function formatBytes(bytes: number): string {
  const mb = bytes / BYTES_PER_MB;
  return mb >= 1024 ? `${roundSize(mb / 1024)} GB` : `${roundSize(mb)} MB`;
}

/** A plan or override value in the limit's own unit; null reads "Unlimited". */
export function formatLimit(limit: PlanLimit, value: number | null): string {
  if (value === null) return 'Unlimited';
  if (limit.unit === 'mb') return formatMb(value);
  if (limit.unit === 'per_minute') return `${value.toLocaleString('en-IN')} / min`;
  return value.toLocaleString('en-IN');
}

/** Short names for usage counters and the tenants-list "Over limit" pill. */
export const USAGE_METRIC_LABELS: Record<UsageMetricSnapshot['metric'], string> = {
  users: 'Users',
  locations: 'Locations',
  invoices: 'Bills',
  reports: 'Reports',
  messages: 'Messages',
  exports: 'Data exports',
  storage_bytes: 'File storage',
  products: 'Products',
};

/** Green under 80 %, amber from 80 %, red from 100 %. Unlimited stays green. */
export function usageTone(percent: number | null): 'success' | 'warning' | 'danger' {
  if (percent === null) return 'success';
  if (percent >= 100) return 'danger';
  if (percent >= 80) return 'warning';
  return 'success';
}
